import React, { Suspense, lazy } from 'react';
import { Card } from '@/designSystem/Card';
import { MapPin, Expand, Users, Building } from 'lucide-react';
import { BaseProperty } from '@/features/property/types';
import { RealtimeProperty } from '../../realtimeProperty/types';
import { ChartCompetitor } from '@/features/charts/types';
import { PROPERTY_COLORS } from '../../property/utils/consts';
import PropertyMap from './PropertyMap';

interface CompactMapProps {
  ownProperty: RealtimeProperty;
  competitors: ChartCompetitor[];
  marketProperties: BaseProperty[];
  onExpandClick: () => void;
}

const MapLoadingPlaceholder: React.FC = () => (
  <div className="flex items-center justify-center h-full w-full bg-gray-50 rounded-lg">
    <div className="text-center">
      <div className="animate-spin h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
      <p className="text-xs text-gray-500">Loading map...</p>
    </div>
  </div>
);

export const CompactMap: React.FC<CompactMapProps> = ({
  ownProperty,
  competitors,
  marketProperties,
  onExpandClick,
}) => {
  const topCompetitors = competitors.slice(0, 3);

  return (
    <Card className="overflow-hidden h-full flex flex-col">
      {/* Header */}
      <div className="bg-blue-50 p-3 border-b border-blue-100 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-blue-600" />
          <div>
            <h3 className="font-semibold text-sm text-gray-900">Competition Map</h3>
            <p className="text-xs text-gray-600">Top competitors nearby</p>
          </div>
        </div>
        <button
          onClick={onExpandClick}
          className="flex items-center gap-1 px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-lg transition-colors"
        >
          <Expand className="w-3 h-3" />
          Expand
        </button>
      </div>

      {/* Stats Bar */}
      <div className="bg-white border-b border-gray-100 px-3 py-2">
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-1" style={{ color: PROPERTY_COLORS.ownProperty.fill }}>
            <span>Your Property</span>
          </div>
          <div className="flex items-center gap-1" style={{ color: PROPERTY_COLORS.competitor.fill }}>
            <Users className="w-3 h-3" />
            <span>{competitors.length} Competitors</span>
          </div>
          <div className="flex items-center gap-1" style={{ color: PROPERTY_COLORS.market.fill }}>
            <Building className="w-3 h-3" />
            <span>{marketProperties.length} Market</span>
          </div>
        </div>
      </div>

      {/* Compact Map */}
      <div className="relative min-h-[400px] h-full bg-gray-100">
        <PropertyMap
          ownProperty={ownProperty}
          competitors={topCompetitors}
          marketProperties={marketProperties.slice(0, 10)} // Limit market properties for performance
        />

        {/* Overlay click area */}
        <div
          className="absolute inset-0 bg-transparent cursor-pointer flex items-center justify-center group"
          onClick={onExpandClick}
        >
          <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 text-white px-3 py-2 rounded-lg text-sm font-medium">
            Click to expand full map
          </div>
        </div>
      </div>
    </Card>
  );
};